import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';

interface ResultsSummaryCardProps {
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  failedRowRanges?: string[];
}

/**
 * Displays a summary card with total, successful, and failed row counts,
 * plus failed row ranges if present.
 */
const ResultsSummaryCard: React.FC<ResultsSummaryCardProps> = ({
  totalRows,
  successfulRows,
  failedRows,
  failedRowRanges,
}) => {
  return (
    <Card variant="outlined" data-testid="results-summary-card">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Import Summary
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            mb: failedRowRanges && failedRowRanges.length > 0 ? 2 : 0,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Rows
            </Typography>
            <Typography variant="h6" data-testid="summary-total">
              {totalRows}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Successful
            </Typography>
            <Typography
              variant="h6"
              color="success.main"
              data-testid="summary-successful"
            >
              {successfulRows}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Failed
            </Typography>
            <Typography
              variant="h6"
              color="error.main"
              data-testid="summary-failed"
            >
              {failedRows}
            </Typography>
          </Box>
        </Box>

        {failedRowRanges && failedRowRanges.length > 0 && (
          <Box data-testid="failed-row-ranges">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Failed Row Ranges
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {failedRowRanges.map((range, index) => (
                <Chip
                  key={index}
                  label={`Rows ${range}`}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ResultsSummaryCard;
